import React, { useContext, useEffect } from 'react';
import {
  Button, CardShowcase,
  Heading,
} from '@americanexpress/dls-react';
import { withRouter } from '@americanexpress/one-app-router';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import Wrapper from '../components/Wrapper';
import CardDetails from '../components/CardDetails';
import { PaymentContext } from '../utility/PaymentContext';
import { paymentTypes } from '../assets/statementData';
import useApiWithSession from '../utility/useApiWithSession';
import { TokenContext } from '../utility/TokenContext';

// Helper to format date string as local date
function formatLocalDate(dateString) {
  if (!dateString) return 'Invalid Date';
  let date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    date = new Date(`${dateString}T00:00:00`);
  } else {
    date = new Date(dateString);
  }
  return !Number.isNaN(date.getTime())
    ? new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(date)
    : 'Invalid Date';
}

function getFilteredPaymentOptions(paymentOptions, paymentTypeMap) {
  return paymentOptions?.defaultPaymentOptions
    ?.filter((option) => option.name !== 'FULL_BALANCE' && option.name !== 'FIXED_AMOUNT')
    ?.map((option) => ({
      titlepay: paymentTypeMap[option.name] || option.name,
      value: `$${Number(option.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    }));
}

const Welcome = ({ router }) => {
  const {
    paymentResponse, market1, journey1, sid1, u1,
  } = useContext(PaymentContext);
  const { gateWayUrl } = useContext(TokenContext);
  const {
    isLoading, data, error, run,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: { eventName: 'WELCOME_LOAD_OK' },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'GUI',
      market: market1,
      journey: journey1,
    },
  });
  const {
    isLoading: isLoadingWelcome, data: dataWelcome, error: errorWelcome, run: run2,
  } = useApiWithSession({
    url: `${gateWayUrl}/voice/v1/multimodal/events`,
    sid1,
    method: 'PUT',
    body: { eventName: 'WELCOME_OK' },
    customHeaders: {
      session_id: sid1,
      correlation_id: u1,
      consumer: 'VUI',
      market: market1,
      journey: journey1,
    },
  });
  const dispatch = useDispatch();

  useEffect(() => {
    const sendWelcomeLoadEvent = async () => {
      try {
        const sessionId = document.cookie
          .split('; ')
          .find((row) => row.startsWith('session_id='))
          ?.split('=')[1];
        if (!sessionId || !paymentResponse) {
          const err = new Error('Session ID is missing or response is missing');
          dispatch(addErrorToReport(err, { message: 'Welcome: Missing session ID or payment response.' }));
          router.push({ pathname: '/message', query: { level: 3 } });
          return;
        }
        await run();
      } catch (er) {
        dispatch(addErrorToReport(er, { message: 'Welcome: WELCOME_LOAD_OK event failed.' }));
      }
    };
    sendWelcomeLoadEvent();
  }, []);

  useEffect(() => {
    if (isLoading === false && (error || data?.statusCode === 'failure')) {
      dispatch(addErrorToReport(error || new Error('WELCOME_LOAD_OK failed'), { message: 'Welcome: WELCOME_LOAD_OK event failed.' }));
      router.push({ pathname: '/message', query: { level: 3 } });
    }
  }, [data?.statusCode, error, isLoading, dispatch, router]);

  useEffect(() => {
    if (!isLoadingWelcome && !errorWelcome && dataWelcome?.statusCode === 'success') {
      router.push({ pathname: '/payyourbill' });
    } else if (isLoadingWelcome === false && (errorWelcome || dataWelcome?.statusCode === 'failure')) {
      dispatch(addErrorToReport(errorWelcome || new Error('WELCOME_OK failed'), { message: 'Welcome: WELCOME_OK event failed.' }));
      router.push({ pathname: '/message', query: { level: 3 } });
    }
  }, [dataWelcome?.statusCode, errorWelcome, isLoadingWelcome, dispatch, router]);

  useEffect(() => {
    // Block browser back/forward navigation
    const handlePopState = () => {
      window.history.go(1);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const filteredOptions = getFilteredPaymentOptions(paymentResponse?.paymentOptions, paymentTypes);

  const handleMakePayment = async () => {
    try {
      const sessionId = document.cookie
        .split('; ')
        .find((row) => row.startsWith('session_id='))
        ?.split('=')[1];
      if (!sessionId) {
        const err = new Error('Session ID is missing.');
        dispatch(addErrorToReport(err, { message: 'Welcome: Missing session ID for payment.' }));
        router.push({ pathname: '/message', query: { level: 3 } });
        return;
      }
      await run2();
    } catch (e) {
      dispatch(addErrorToReport(e, { message: 'Welcome: WELCOME_OK event failed.' }));
    }
  };

  const paymentDueDate = paymentResponse?.paymentOptions?.paymentDueDate;
  const paymentDueAmount = paymentResponse?.paymentOptions?.defaultPaymentOptions?.find((option) => option.name === 'PAYMENT_DUE')?.amount;
  const minimumDueAmount = paymentResponse?.paymentOptions?.defaultPaymentOptions?.find((option) => option.name === 'MINIMUM_DUE')?.amount;
  const fullBalanceAmount = paymentResponse?.paymentOptions?.defaultPaymentOptions?.find((option) => option.name === 'FULL_BALANCE')?.amount;

  const isPaymentRequired = !(paymentDueAmount <= 0 || minimumDueAmount <= 0);

  return (
    <div>
      <Wrapper>
        <div className="margin-4-t margin-2-b">
          <Heading level={4} headingStyle="heading4" style={{ fontWeight: 500 }} className="text-align-center">
            {paymentResponse?.productInfo?.product_desc}
          </Heading>
          <p className="label-1 text-align-center">{paymentResponse?.productInfo?.display_account_number}</p>
        </div>
        <CardDetails imageSource={paymentResponse?.productInfo?.digital_asset_url} />
        <CardShowcase className="anim-fade-in anim-delay-1 width-full pad-1 card-rounded">
          <div className="pad-1-lr pad-2-tb card-rounded">
            <div className="flex flex-column flex-align-start">
              {!isPaymentRequired ? (
                <p className="heading-3">No payment is required at this time</p>
              ) : (
                <>
                  <p className="body-3">Total Balance</p>
                  <Heading level={4} headingStyle="heading5" style={{ fontWeight: 700 }}>
                    ${Number(fullBalanceAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Heading>
                </>
              )}
              <div className="margin-2-t flex flex-column flex-align-start width-full" style={{ gap: '10px' }}>
                <p className="heading-3">
                  Your payment is due on {formatLocalDate(paymentDueDate)}
                </p>
                {filteredOptions?.map((option) => (
                  <div key={option.titlepay} className="flex flex-justify-between width-full">
                    <p className="body-3 text-align-left dls-gray-05">{option.titlepay}</p>
                    <p className="body-3">{option.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardShowcase>
      </Wrapper>
      <div className="width-full pad-1">
        <Button
          type="button"
          className="width-full"
          styleType="primary"
          style={{ maxWidth: '100%' }}
          onClick={handleMakePayment}
        >
          <p className="heading-4">Make a Payment</p>
        </Button>
      </div>
    </div>
  );
};

export default withRouter(Welcome);

Welcome.propTypes = {
  router: PropTypes.instanceOf(Object).isRequired,
};
